import React from 'react';
import {
  Button,
  notifications,
  Stack,
  Body,
  Link,
  Box,
  Divider,
  useWindowProperties,
} from '@qred/components-library';
import { confirmContractAction } from '~/store/actions/additionalSignerActions';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '~/hooks/useAppDispatch';
import useTranslate from '~/hooks/useTranslate';
import { ApiStatus } from '~/store/types/sharedTypes';
import { RootState } from '~/store';
import { logToSentry } from '~/helpers/loggers.helper';
import useTermsAndConditionsModal from '~/hooks/useTermsAndConditionsModal';
import { CountryCode, LoanType } from '~/enums';

const LoanAdditionalSignerFooter = () => {
  const {
    additionalSigner: {
      additionalSignerLoanApplicationOffer: { granter, loan_type },
      apiStatus: { postAdditionalSignerLoanApplication },
    },
    intl: { market },
  } = useSelector((state: RootState) => state);

  const dispatch = useAppDispatch();
  const translate = useTranslate();
  const { openTermsAndConditionsModal } = useTermsAndConditionsModal();
  const { isMobile } = useWindowProperties();

  const onConfirmLoanClick = () => {
    dispatch(confirmContractAction());
  };

  const openModal = () => {
    if (granter) {
      openTermsAndConditionsModal(granter);
    } else {
      notifications.show({
        state: 'critical',
        message: translate('SomethingWentWrong'),
        opened: true,
      });
      logToSentry(
        new Error('Granter is missing for application'),
        'GranterIsMissingForApplication'
      );
    }
  };

  const isNorwayAnnuityLoan =
    market === CountryCode.NO && loan_type === LoanType.ANNUITY_LOAN;

  const openTermsExternal = () => {
    window.open(
      'https://www.qred.no/vilkar-og-betingelser',
      '_blank',
      'noopener noreferrer'
    );
  };

  const openPriceListExternal = () => {
    window.open(
      'https://www.qred.no/bedriftslan-prisliste',
      '_blank',
      'noopener noreferrer'
    );
  };

  const renderAgreementText = () => {
    if (isNorwayAnnuityLoan) {
      const termsAndConditionsLink = (
        <Link size="sm" onClick={openTermsExternal}>
          {translate('OnboardingOffer.TermsAndConditionslinkText')}
        </Link>
      );

      const priceListingLink = (
        <Link size="sm" onClick={openPriceListExternal}>
          {translate('OnboardingOffer.PriceListingLinkText')}
        </Link>
      );

      return translate(
        'OnboardingOffer.TermsAndConditionsConsentWithPriceList',
        {
          termsAndConditionslinkText: termsAndConditionsLink,
          priceListingLinkText: priceListingLink,
        }
      ) as string;
    }

    return (
      <>
        {translate('ConfirmTermsCard.UserAgreementText')}
        <Link size="sm" onClick={openModal} ml={'xs'}>
          {translate('ConfirmTermsCard.UserAgreementLink')}
        </Link>
      </>
    );
  };

  return (
    <Box>
      <Divider thickness={1} color="neutral.16" />
      <Stack spacing={'md'} align="center" p={isMobile ? 'md' : 'xxl'}>
        <Body size="sm">{renderAgreementText()}</Body>
        <Button
          fullWidth={isMobile}
          onClick={onConfirmLoanClick}
          size="md"
          isLoading={postAdditionalSignerLoanApplication === ApiStatus.Started}
        >
          {translate('ConfirmTermsCard.ConfirmAgreement')}
        </Button>
      </Stack>
    </Box>
  );
};

export default LoanAdditionalSignerFooter;
